/**
 * Whether the corner cluster is on screen, and the one place that decides.
 *
 * Five controls live permanently in the bottom corner — the chat bubble, the
 * sound toggle, the language switch, the theme switch, and the fold that this
 * module is about. Every one of them is there for a reason that has been
 * argued out in its own file, and the reason is always the same shape: there
 * is no settings page in this product, so a control that is wanted from the
 * middle of a level has to be in the corner or it does not exist.
 *
 * What that argument never accounted for is the person who is *reading*. A
 * chapter essay runs to the foot of the window, a debrief runs past it, and
 * the last two lines of both sit under two hundred pixels of interface that
 * the reader has no use for at that moment. The answer is not to move the
 * controls somewhere worse; it is to let them go away, and to make coming
 * back cost one click on something that is still visibly there.
 *
 * So this is a preference, not state: the fold is remembered per browser and
 * outlives the visit, the way the theme and the language do. Somebody who
 * reads with the corner empty gets it empty next time.
 *
 * Deliberately not React state, for the reasons in lib/theme.ts: the answer
 * is applied as an attribute on <html> and the whole cluster follows it
 * through CSS, so no component has to thread it anywhere. The three toggles
 * do not know this module exists.
 *
 * No `?corner=` in the URL, unlike the theme and the language. Those two are
 * in the address bar because they make a link shareable — a lecturer putting
 * this on a projector, a bug report about Arabic. Nobody shares a link to a
 * folded corner.
 */

export const CORNERS = ['shown', 'folded'] as const;

export type Corner = (typeof CORNERS)[number];

/** The cluster is there on a first visit. Being findable is the default. */
export const DEFAULT_CORNER: Corner = 'shown';

export function isCorner(value: unknown): value is Corner {
  return typeof value === 'string' && (CORNERS as readonly string[]).includes(value);
}

const KEY = 'engineeros.corner';

/**
 * There is a document to paint. False under `node --test`, where everything
 * below degrades to "shown, and nothing painted" — the same arrangement
 * lib/theme.ts and lib/locale.ts have, and for the same reason.
 */
const inBrowser = typeof document !== 'undefined' && typeof window !== 'undefined';

function fromStorage(): Corner | null {
  if (!inBrowser) return null;
  try {
    const saved = window.localStorage.getItem(KEY);
    return isCorner(saved) ? saved : null;
  } catch {
    // Private mode, or storage switched off. Not knowing means shown, which
    // is the state in which everything is reachable.
    return null;
  }
}

let current: Corner = fromStorage() ?? DEFAULT_CORNER;

const listeners = new Set<(corner: Corner) => void>();

/**
 * Put the answer on the document, for both values rather than only for the
 * folded one, on lib/theme.ts's reasoning: a DOM that says `data-corner`
 * either way is answerable from the devtools and from a screenshot, where an
 * absent attribute is ambiguous between "shown" and "this build cannot fold".
 */
function paint(corner: Corner): void {
  if (!inBrowser) return;
  document.documentElement.dataset.corner = corner;
}

// During module evaluation, before React exists. There is no inline copy of
// this in index.html, and it needs none: the cluster is drawn by the app, so
// the attribute is on the document before anything it governs has rendered.
paint(current);

export function getCorner(): Corner {
  return current;
}

/** True when the cluster is tucked into the edge. */
export function isFolded(): boolean {
  return current === 'folded';
}

/**
 * Fold the cluster away, or bring it back.
 *
 * Quiet when nothing changed, like `setTheme` and `setLocale`: this is called
 * from a control that can be clicked twice and from the chat dock, which
 * unfolds the corner whenever the panel opens.
 */
export function setCorner(corner: Corner): void {
  if (corner === current) return;
  current = corner;
  paint(corner);
  try {
    if (inBrowser) window.localStorage.setItem(KEY, corner);
  } catch {
    // The choice still holds for this visit; it just will not outlive it.
  }
  for (const listener of listeners) listener(corner);
}

/** Subscribe to the fold. Returns the unsubscribe. */
export function onCorner(listener: (corner: Corner) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The other state, for a control that has exactly two. */
export function otherCorner(corner: Corner = current): Corner {
  return corner === 'shown' ? 'folded' : 'shown';
}

/* --- Something waiting behind the fold ------------------------------------ */

/**
 * Whether anything in the folded cluster wants attention.
 *
 * One thing in the corner can change while nobody is looking at it, and it is
 * the chat bubble: a message arrives, the bubble grows a count and pings its
 * ring, and if the cluster is folded none of that is on screen. Folding is
 * meant to quieten the corner, not to silently leave the room — so the dock
 * reports its unread count here (ChatDock.tsx) and the fold carries a dot.
 *
 * A flag rather than a number: the fold is 20px wide and the count is already
 * said properly on the bubble the dot brings back. And per visit rather than
 * per browser — it is a fact about the room right now, not a preference —
 * which is why nothing about it is stored.
 */
let waiting = false;

const signals = new Set<(waiting: boolean) => void>();

export function getCornerSignal(): boolean {
  return waiting;
}

export function setCornerSignal(next: boolean): void {
  if (next === waiting) return;
  waiting = next;
  for (const listener of signals) listener(next);
}

/** Subscribe to the signal. Returns the unsubscribe. */
export function onCornerSignal(listener: (waiting: boolean) => void): () => void {
  signals.add(listener);
  return () => signals.delete(listener);
}
